import React from 'react';

import {
	FormattedDate,
	FormattedMessage,
	FormattedRelativeTime,
	type IntlShape,
	type MessageDescriptor,
} from 'react-intl-next';

import { selectUnit } from '@atlassian/recent-work-ui';

import { isMoreThanOneWeekAgo } from '../../../../../common/ui/event-type-message';

import {
	clickedEventTypeMessages,
	commentedEventTypeMessages,
	editedEventTypeMessages,
	type EventTypeMessages,
	messages,
	viewedEventTypeMessages,
	wereMentionedEventTypeMessages,
} from './messages';
import { parseRecommendedReason, RecommendedEventType, RecommendedType } from './types';

const LOCAL_EVENT_TYPE_MESSAGES: Record<RecommendedEventType, EventTypeMessages> = {
	clicked: clickedEventTypeMessages,
	commented: commentedEventTypeMessages,
	edited: editedEventTypeMessages,
	viewed: viewedEventTypeMessages,
	'were mentioned': wereMentionedEventTypeMessages,
};

/**
 * Returns the appropriate message descriptor for a given event type.
 * @param eventType - The type of the event.
 * @param options - Options to configure the message.
 * @returns The message descriptor for the event type.
 */
const messageForEventType = (
	eventType: RecommendedEventType,
	options: { isAbsolute?: boolean } = {},
): MessageDescriptor => {
	const { isAbsolute = true } = options;
	const eventTypeMessage = LOCAL_EVENT_TYPE_MESSAGES.hasOwnProperty(eventType)
		? LOCAL_EVENT_TYPE_MESSAGES[eventType]
		: viewedEventTypeMessages; // default to viewed messages

	return isAbsolute ? eventTypeMessage.absolute : eventTypeMessage.relative;
};

// Sort enum values by length descending so multi-word values (e.g. "were mentioned")
// are matched before shorter substrings (e.g. "mentioned")
const sortedRecommendedEventTypes = Object.values(RecommendedEventType).sort(
	(a, b) => b.length - a.length,
);

/**
 * Extracts the event type from `reason.text` by checking if the text contains
 * a known event type keyword. Returns the matching `RecommendedEventType` value,
 * or `RecommendedEventType.VIEWED` as a default fallback.
 */
export const extractEventTypeFromReasonText = (text: string): RecommendedEventType => {
	const lowerText = text.toLowerCase();

	for (const eventType of sortedRecommendedEventTypes) {
		if (lowerText.includes(eventType)) {
			return eventType;
		}
	}

	return RecommendedEventType.VIEWED;
};

type UserDocumentEventTypeMessageProps = {
	reason: string;
	intl: IntlShape;
	logException: (error: Error) => Promise<void>;
};

export const UserDocumentEventTypeMessage = ({
	reason,
	intl,
	logException,
}: UserDocumentEventTypeMessageProps): React.ReactNode => {
	const parsedReason = parseRecommendedReason(reason, logException);

	if (!parsedReason) {
		return null;
	}

	const eventType = extractEventTypeFromReasonText(parsedReason.text);

	const { type, timestamp } = parsedReason;

	const collaborator =
		type === RecommendedType.NO_COLLABORATOR
			? intl.formatMessage(messages.you)
			: intl.formatMessage(messages.collaborator);

	const isAbsolute = isMoreThanOneWeekAgo(timestamp);

	const message = messageForEventType(eventType, {
		isAbsolute,
	});

	const { value, unit } = selectUnit(new Date(timestamp).getTime(), new Date(), {
		day: 7,
	});

	const values = isAbsolute
		? {
				// Show the absolute time if the activity was more than a week ago
				// e.g: You viewed on January 1, 2021
				absoluteTime: (
					<FormattedDate value={timestamp} month="short" day="2-digit" year="numeric" />
				),
			}
		: {
				// Show the relative time if the activity was within a week ago
				// e.g: You viewed 2 hours ago
				relativeTime: <FormattedRelativeTime value={value} unit={unit} />,
			};

	return <FormattedMessage {...message} values={{ ...values, collaborator }} />;
};
